// Ranked Match Configuration

import { GameMode } from "../../types/enum/Game"

// Entry fee in USDC
export const RANKED_ENTRY_FEE = 1.0

// Whale Ranked entry fee in USDC
export const WHALE_RANKED_ENTRY_FEE = 100.0

// Auto-start timeout in milliseconds (1 minute)
export const RANKED_AUTO_START_TIMEOUT = 60 * 1000

// Prize distribution by number of paying players
// Keys are player ranks (1-8), values are prizes in USDC
// "fee" is the amount sent to the fee wallet
export interface PrizeDistribution {
  [rank: number]: number
  fee: number
}

export const PRIZE_DISTRIBUTION: { [playerCount: number]: PrizeDistribution } = {
  8: {
    1: 2.0,
    2: 1.75,
    3: 1.50,
    4: 1.25,
    5: 0.50,
    6: 0.50,
    7: 0,
    8: 0,
    fee: 0.5
  },
  7: {
    1: 2.0,
    2: 1.75,
    3: 1.25,
    4: 1.0,
    5: 0.5,
    6: 0,
    7: 0,
    fee: 0.5
  },
  6: {
    1: 2.0,
    2: 1.5,
    3: 1.0,
    4: 1.0,
    5: 0,
    6: 0,
    fee: 0.5
  },
  5: {
    1: 2.0,
    2: 1.5,
    3: 1.0,
    4: 0,
    5: 0,
    fee: 0.5
  },
  4: {
    1: 1.4,
    2: 1.25,
    3: 0.75,
    4: 0.4,
    fee: 0.2
  },
  3: {
    1: 1.4,
    2: 1.0,
    3: 0.4,
    fee: 0.2
  },
  2: {
    1: 1.4,
    2: 0.4,
    fee: 0.2
  },
  1: {
    1: 1.0, // Return deposit when solo
    fee: 0
  }
}

// Whale Ranked prize distribution (100x of regular)
export const WHALE_PRIZE_DISTRIBUTION: { [playerCount: number]: PrizeDistribution } = {
  8: {
    1: 200,
    2: 175,
    3: 150,
    4: 125,
    5: 50,
    6: 50,
    7: 0,
    8: 0,
    fee: 50
  },
  7: {
    1: 200,
    2: 175,
    3: 125,
    4: 100,
    5: 50,
    6: 0,
    7: 0,
    fee: 50
  },
  6: {
    1: 200,
    2: 150,
    3: 100,
    4: 100,
    5: 0,
    6: 0,
    fee: 50
  },
  5: {
    1: 200,
    2: 150,
    3: 100,
    4: 0,
    5: 0,
    fee: 50
  },
  4: {
    1: 140,
    2: 125,
    3: 75,
    4: 40,
    fee: 20
  },
  3: {
    1: 140,
    2: 100,
    3: 40,
    fee: 20
  },
  2: {
    1: 140,
    2: 40,
    fee: 20
  },
  1: {
    1: 100, // Return deposit when solo
    fee: 0
  }
}

// Ranked Duos entry fee (same as regular ranked)
export const RANKED_DUOS_ENTRY_FEE = 1.0
// Duos uses the same PRIZE_DISTRIBUTION as regular ranked, keyed by total human player count.
// Teams are ranked, and each human on a team occupies consecutive rank slots.
// Teammates share the average prize of their team's allocated slots.

// Helper: check if a game mode is a duo mode
export function isDuoMode(gameMode: GameMode): boolean {
  return gameMode === GameMode.DUOS || gameMode === GameMode.RANKED_DUOS
}

// Helper: check if a game mode is any ranked variant
export function isRankedMode(gameMode: GameMode): boolean {
  return gameMode === GameMode.RANKED || gameMode === GameMode.WHALE_RANKED || gameMode === GameMode.RANKED_DUOS
}

// Helper: get entry fee for a ranked mode
export function getEntryFeeForMode(gameMode: GameMode): number {
  if (gameMode === GameMode.WHALE_RANKED) return WHALE_RANKED_ENTRY_FEE
  if (gameMode === GameMode.RANKED) return RANKED_ENTRY_FEE
  if (gameMode === GameMode.RANKED_DUOS) return RANKED_DUOS_ENTRY_FEE
  return 0
}

// Get the prize distribution table for a game mode
function getDistributionForMode(gameMode: GameMode): { [playerCount: number]: PrizeDistribution } {
  if (gameMode === GameMode.WHALE_RANKED) return WHALE_PRIZE_DISTRIBUTION
  return PRIZE_DISTRIBUTION
}

// Get prize for a specific rank given player count
export function getPrizeForRank(playerCount: number, rank: number, gameMode: GameMode = GameMode.RANKED): number {
  const distribution = getDistributionForMode(gameMode)[playerCount]
  if (!distribution) return 0
  return distribution[rank] || 0
}

// Get fee for the fee wallet given player count
export function getFeeForPlayerCount(playerCount: number, gameMode: GameMode = GameMode.RANKED): number {
  const distribution = getDistributionForMode(gameMode)[playerCount]
  if (!distribution) return 0
  return distribution.fee
}

// ELO Tier Configuration (percentile-based)
export interface EloTierConfig {
  topN: number | null // Top N players qualify (null = no limit)
  percentile: number  // Top X% qualify
}

export const ELO_TIERS: { [tier: string]: EloTierConfig } = {
  MASTER_BALL: { topN: 100, percentile: 5 },
  ULTRA_BALL: { topN: 1000, percentile: 15 },
  GREAT_BALL: { topN: 10000, percentile: 30 },
  POKE_BALL: { topN: null, percentile: 50 } // Bottom 50%
}

export enum EloTier {
  POKE_BALL = "POKE_BALL",
  GREAT_BALL = "GREAT_BALL",
  ULTRA_BALL = "ULTRA_BALL",
  MASTER_BALL = "MASTER_BALL"
}

// Display order (highest to lowest)
export const ELO_TIER_ORDER: EloTier[] = [
  EloTier.MASTER_BALL,
  EloTier.ULTRA_BALL,
  EloTier.GREAT_BALL,
  EloTier.POKE_BALL
]
